import { useAuthStore } from '@/stores';

// API URL - Direct API endpoint (v2)
// Updated: 2026-03-18 - Force cache refresh
const API_BASE = 'https://memories-api.haliagav.workers.dev/api';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  skipAuth?: boolean;
}

export async function apiClient<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, skipAuth = false } = options;

  // Obtener token del store
  const token = !skipAuth ? useAuthStore.getState().token : null;

  // Detectar si el body es FormData
  const isFormData = body instanceof FormData;

  // Construir headers manualmente
  const requestHeaders: Record<string, string> = {};
  
  // Agregar token si existe
  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }
  
  // Solo agregar Content-Type si NO es FormData
  // El navegador debe manejar FormData automáticamente con el boundary correcto
  if (!isFormData) {
    requestHeaders['Content-Type'] = 'application/json';
  }
  
  // Agregar headers adicionales (pero NO sobrescribir Content-Type si es FormData)
  Object.entries(headers).forEach(([key, value]) => {
    if (key.toLowerCase() === 'content-type' && isFormData) {
      // Ignorar Content-Type personalizado cuando es FormData
      return;
    }
    requestHeaders[key] = value;
  });

  const config: RequestInit = {
    method,
    headers: requestHeaders,
    credentials: 'include',
  };

  if (body) {
    // Si es FormData, enviar directamente, si no, stringify
    config.body = isFormData ? body : JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE}${endpoint}`, config);

  // Si retorna 401 y tenemos token, hacer logout
  if (response.status === 401 && token && !skipAuth) {
    useAuthStore.getState().logout();
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export const api = {
  get: <T>(endpoint: string) => apiClient<T>(endpoint),
  
  post: <T>(endpoint: string, body: unknown) => 
    apiClient<T>(endpoint, { method: 'POST', body }),
  
  put: <T>(endpoint: string, body: unknown) => 
    apiClient<T>(endpoint, { method: 'PUT', body }),
  
  patch: <T>(endpoint: string, body: unknown) => 
    apiClient<T>(endpoint, { method: 'PATCH', body }),
  
  delete: <T>(endpoint: string) => 
    apiClient<T>(endpoint, { method: 'DELETE' }),
};

export const documentsApi = {
  list: (workspaceId: string) => 
    api.get<{ success: boolean; documents: any[] }>(`/documents?workspaceId=${workspaceId}`),
  
  get: (id: string) => 
    api.get<{ success: boolean; document: any }>(`/documents/${id}`),
  
  create: (data: { title: string; workspaceId: string; type?: string; parentId?: string | null }) => 
    api.post<{ success: boolean; document: any }>('/documents', data),
  
  update: (data: { id: string; [key: string]: any }) => 
    api.put<{ success: boolean; document: any }>('/documents', data),
  
  delete: (id: string) => 
    api.delete<{ success: boolean }>(`/documents?id=${id}`),
};

export const workspacesApi = {
  list: () => 
    api.get<{ success: boolean; workspaces: any[] }>('/workspaces'),
  
  create: (data: { name: string }) => 
    api.post<{ success: boolean; workspace: any }>('/workspaces', data),
  
  update: (data: { id: string; name: string }) => 
    api.put<{ success: boolean; workspace: any }>('/workspaces', data),
  
  delete: (id: string) => 
    api.delete<{ success: boolean }>(`/workspaces?id=${id}`),
};

export const authApi = {
  login: (email: string, password: string) => 
    api.post<{ success: boolean; user: any; token: string }>('/auth/login', { email, password }),
  
  register: (name: string, email: string, password: string) => 
    api.post<{ success: boolean; user: any; token: string }>('/auth/register', { name, email, password }),
  
  me: () => 
    api.get<{ success: boolean; user: any }>('/auth/me'),
  
  logout: () => 
    api.post<{ success: boolean }>('/auth/logout', {}),
  
  updateProfile: (data: { name?: string }) => 
    api.put<{ success: boolean; user: any }>('/auth/profile', data),
  
  changePassword: (currentPassword: string, newPassword: string) => 
    api.put<{ success: boolean }>('/auth/password', { currentPassword, newPassword }),
};

export const shareApi = {
  list: (documentId: string) => 
    api.get<{ success: boolean; shares: any[] }>(`/share?documentId=${documentId}`),
  
  create: (data: { documentId: string; isPublic?: boolean; shareType?: string; canEdit?: boolean; canDownload?: boolean; password?: string; expiresIn?: number }) => 
    api.post<{ success: boolean; share: { token: string } }>('/share', data),
  
  delete: (id: string) => 
    api.delete<{ success: boolean }>(`/share?id=${id}`),
};
